// src/components/settings/ProfileTab.tsx
import React, { useState } from "react";
import {
  Card,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Badge,
  IconButton,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Save as SaveIcon,
  Camera as CameraIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { gradients } from "../../theme/theme";
import AvatarUploadModal from "./AvatarUploadModal";

// Constants
const COMPANY_NAME = "LocateNow, LLC";

// Types
export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  company_name: string;
  job_title: string;
  avatar_url: string | null;
}

export interface ValidationErrors {
  phone?: string;
  email?: string;
  agentPhone?: string;
  agentEmail?: string;
}

// Validation helpers
export const validatePhone = (phone: string): boolean => {
  const phoneRegex =
    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  return phone === "" || phoneRegex.test(phone.replace(/\s/g, ""));
};

export const formatPhoneNumber = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
      6,
      10
    )}`;
  }
};

interface ProfileTabProps {
  profile: UserProfile;
  setProfile: React.Dispatch<React.SetStateAction<UserProfile>>;
  validationErrors: ValidationErrors;
  setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
  loading: boolean;
  onSave: () => Promise<void>;
}

const ProfileTab: React.FC<ProfileTabProps> = ({
  profile,
  setProfile,
  validationErrors,
  setValidationErrors,
  loading,
  onSave,
}) => {
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  const handleAvatarChange = (url: string | null) => {
    setProfile((prev) => ({ ...prev, avatar_url: url }));
  };

  const handlePhoneChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    setProfile((prev) => ({ ...prev, phone: formatted }));

    if (formatted && !validatePhone(formatted)) {
      setValidationErrors((prev) => ({
        ...prev,
        phone: "Please enter a valid phone number",
      }));
    } else {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.phone;
        return newErrors;
      });
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card sx={{ p: 3, textAlign: "center" }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <IconButton
                size="small"
                onClick={() => setAvatarModalOpen(true)}
                aria-label="Change profile picture"
                sx={{
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": { backgroundColor: "primary.dark" },
                }}
              >
                <CameraIcon fontSize="small" />
              </IconButton>
            }
          >
            <Avatar
              src={profile.avatar_url || undefined}
              onClick={() => setAvatarModalOpen(true)}
              sx={{
                width: 120,
                height: 120,
                fontSize: 48,
                background: gradients.primary,
                mx: "auto",
                cursor: "pointer",
              }}
            >
              {profile.first_name?.[0]}
              {profile.last_name?.[0]}
            </Avatar>
          </Badge>
          <AvatarUploadModal
            open={avatarModalOpen}
            onClose={() => setAvatarModalOpen(false)}
            userId={profile.id}
            currentAvatarUrl={profile.avatar_url}
            initials={`${profile.first_name?.[0] ?? ""}${profile.last_name?.[0] ?? ""}`}
            onChange={handleAvatarChange}
          />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {profile.first_name} {profile.last_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {profile.email}
          </Typography>
          {profile.job_title && (
            <Chip label={profile.job_title} size="small" sx={{ mt: 1 }} />
          )}
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            {COMPANY_NAME}
          </Typography>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={profile.first_name}
                onChange={(e) =>
                  setProfile({ ...profile, first_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={profile.last_name}
                onChange={(e) =>
                  setProfile({ ...profile, last_name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={profile.email}
                disabled
                helperText="Email cannot be changed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={profile.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                error={!!validationErrors.phone}
                helperText={validationErrors.phone || "Format: (XXX) XXX-XXXX"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ maxLength: 14 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Title"
                value={profile.job_title}
                onChange={(e) =>
                  setProfile({ ...profile, job_title: e.target.value })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={COMPANY_NAME}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Company name is set by your organization"
              />
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={onSave}
              disabled={loading || !!validationErrors.phone}
            >
              Save Profile
            </Button>
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ProfileTab;
